import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { DomainRegistrationType } from '../enums/domain-registration-type';
import { DomainSuggestionType } from '../enums/domain-suggestion-type';
export class Domains {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * List all domains registered for this project. This endpoint supports pagination.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on attributes such as domain name, teamInternalId, expiration, etc.
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainsList>}
     */
    list(params?: {
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.DomainsList>;
    /**
     * List all domains registered for this project. This endpoint supports pagination.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on attributes such as domain name, teamInternalId, expiration, etc.
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    list(
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.DomainsList>;
    list(
        paramsOrFirst?:
            { queries?: string[]; search?: string; total?: boolean } | string[],
        ...rest: [string?, boolean?]
    ): Promise<Models.DomainsList> {
        let params: { queries?: string[]; search?: string; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                search: rest[0] as string,
                total: rest[1] as boolean,
            };
        }

        const queries = params.queries;
        const search = params.search;
        const total = params.total;

        const apiPath = '/domains';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainsList',
            models: {
                domainsList: {
                    defaults: {
                        total: 0,
                        domains: [],
                    },
                    properties: {
                        domains: {
                            items: 'domain',
                        },
                    },
                },
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new domain. Before creating a domain, you need to ensure that your DNS provider is properly configured. After creating the domain, you can use the verification endpoint to check if the domain is ready to be used.
     *
     * @param {string} params.teamId - Team unique ID.
     * @param {string} params.domain - Domain name (e.g. "example.com").
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    create(params: { teamId: string; domain: string }): Promise<Models.Domain>;
    /**
     * Create a new domain. Before creating a domain, you need to ensure that your DNS provider is properly configured. After creating the domain, you can use the verification endpoint to check if the domain is ready to be used.
     *
     * @param {string} teamId - Team unique ID.
     * @param {string} domain - Domain name (e.g. "example.com").
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    create(teamId: string, domain: string): Promise<Models.Domain>;
    create(
        paramsOrFirst: { teamId: string; domain: string } | string,
        ...rest: [string?]
    ): Promise<Models.Domain> {
        let params: { teamId: string; domain: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                teamId: string;
                domain: string;
            };
        } else {
            params = {
                teamId: paramsOrFirst as string,
                domain: rest[0] as string,
            };
        }

        const teamId = params.teamId;
        const domain = params.domain;

        if (typeof teamId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "teamId"');
        }
        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        const apiPath = '/domains';
        const apiPayload: Payload = {};
        if (typeof teamId !== 'undefined') {
            apiPayload['teamId'] = teamId;
        }
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the registration price for a domain name.
     *
     * @param {string} params.domain - Domain name to get price for.
     * @param {number} params.periodYears - Number of years to calculate the domain price for. Must be at least 1.
     * @param {DomainRegistrationType} params.registrationType - Type of registration pricing to fetch. Allowed values: new, transfer, renewal, trade.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPrice>}
     * @deprecated This API has been deprecated since 2.0.0. Please use `Domains.listPrices` instead.
     */
    getPrice(params: {
        domain: string;
        periodYears?: number;
        registrationType?: DomainRegistrationType;
    }): Promise<Models.DomainPrice>;
    /**
     * Get the registration price for a domain name.
     *
     * @param {string} domain - Domain name to get price for.
     * @param {number} periodYears - Number of years to calculate the domain price for. Must be at least 1.
     * @param {DomainRegistrationType} registrationType - Type of registration pricing to fetch. Allowed values: new, transfer, renewal, trade.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPrice>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPrice(
        domain: string,
        periodYears?: number,
        registrationType?: DomainRegistrationType,
    ): Promise<Models.DomainPrice>;
    getPrice(
        paramsOrFirst:
            | {
                  domain: string;
                  periodYears?: number;
                  registrationType?: DomainRegistrationType;
              }
            | string,
        ...rest: [number?, DomainRegistrationType?]
    ): Promise<Models.DomainPrice> {
        let params: {
            domain: string;
            periodYears?: number;
            registrationType?: DomainRegistrationType;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                periodYears?: number;
                registrationType?: DomainRegistrationType;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                periodYears: rest[0] as number,
                registrationType: rest[1] as DomainRegistrationType,
            };
        }

        const domain = params.domain;
        const periodYears = params.periodYears;
        const registrationType = params.registrationType;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        const apiPath = '/domains/price';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof periodYears !== 'undefined') {
            apiPayload['periodYears'] = periodYears;
        }
        if (typeof registrationType !== 'undefined') {
            apiPayload['registrationType'] = registrationType;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPrice',
            models: {
                domainPrice: {
                    defaults: {
                        domain: '',
                        tld: '',
                        available: false,
                        periodYears: 1,
                        premium: false,
                        renewalPeriodYears: 1,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Check availability and get the requested registration price for one or more domain names. Availability is resolved for all domains in a single registrar lookup. Unavailable domains have a null price for new registrations, but can still be priced for renewal, transfer, or trade. Every priced domain also carries its renewal price for the same period, so a separate renewal lookup is not needed. A domain whose price could not be resolved, for example because its TLD is not supported, is returned with a null price.
     *
     * @param {string[]} params.domains - Domain names to check availability and price for. Maximum of 50 domains per request.
     * @param {number} params.periodYears - Number of years to calculate the domain price for. Must be at least 1.
     * @param {DomainRegistrationType} params.registrationType - Type of registration pricing to fetch. Allowed values: new, transfer, renewal, trade.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPricesList>}
     */
    listPrices(params: {
        domains: string[];
        periodYears?: number;
        registrationType?: DomainRegistrationType;
    }): Promise<Models.DomainPricesList>;
    /**
     * Check availability and get the requested registration price for one or more domain names. Availability is resolved for all domains in a single registrar lookup. Unavailable domains have a null price for new registrations, but can still be priced for renewal, transfer, or trade. Every priced domain also carries its renewal price for the same period, so a separate renewal lookup is not needed. A domain whose price could not be resolved, for example because its TLD is not supported, is returned with a null price.
     *
     * @param {string[]} domains - Domain names to check availability and price for. Maximum of 50 domains per request.
     * @param {number} periodYears - Number of years to calculate the domain price for. Must be at least 1.
     * @param {DomainRegistrationType} registrationType - Type of registration pricing to fetch. Allowed values: new, transfer, renewal, trade.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPricesList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listPrices(
        domains: string[],
        periodYears?: number,
        registrationType?: DomainRegistrationType,
    ): Promise<Models.DomainPricesList>;
    listPrices(
        paramsOrFirst:
            | {
                  domains: string[];
                  periodYears?: number;
                  registrationType?: DomainRegistrationType;
              }
            | string[],
        ...rest: [number?, DomainRegistrationType?]
    ): Promise<Models.DomainPricesList> {
        let params: {
            domains: string[];
            periodYears?: number;
            registrationType?: DomainRegistrationType;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domains: string[];
                periodYears?: number;
                registrationType?: DomainRegistrationType;
            };
        } else {
            params = {
                domains: paramsOrFirst as string[],
                periodYears: rest[0] as number,
                registrationType: rest[1] as DomainRegistrationType,
            };
        }

        const domains = params.domains;
        const periodYears = params.periodYears;
        const registrationType = params.registrationType;

        if (typeof domains === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "domains"',
            );
        }
        const apiPath = '/domains/prices';
        const apiPayload: Payload = {};
        if (typeof domains !== 'undefined') {
            apiPayload['domains'] = domains;
        }
        if (typeof periodYears !== 'undefined') {
            apiPayload['periodYears'] = periodYears;
        }
        if (typeof registrationType !== 'undefined') {
            apiPayload['registrationType'] = registrationType;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPricesList',
            models: {
                domainPricesList: {
                    defaults: {
                        total: 0,
                        prices: [],
                    },
                    properties: {
                        prices: {
                            items: 'domainPrice',
                        },
                    },
                },
                domainPrice: {
                    defaults: {
                        domain: '',
                        tld: '',
                        available: false,
                        periodYears: 1,
                        premium: false,
                        renewalPeriodYears: 1,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Initiate a domain purchase by providing registrant details and a payment method. Authorizes the payment and returns a `clientSecret`. If 3D Secure is required, use the `clientSecret` on the client to complete the authentication challenge. Once authentication is complete (or if none is needed), call the Update Purchase endpoint to capture the payment and finalize the purchase.
     *
     * @param {string} params.domain - Fully qualified domain name to purchase (for example, example.com).
     * @param {string} params.organizationId - Team ID that will own the domain.
     * @param {string} params.firstName - Registrant first name used for domain registration.
     * @param {string} params.lastName - Registrant last name used for domain registration.
     * @param {string} params.email - Registrant email address for registration and notices.
     * @param {string} params.phone - Registrant phone number in E.164 format (for example, +15555551234).
     * @param {string} params.billingAddressId - Billing address ID used for registration contact details.
     * @param {string} params.paymentMethodId - Payment method ID to authorize and capture the purchase.
     * @param {string} params.addressLine3 - Additional address line for the registrant (line 3).
     * @param {string} params.companyName - Company or organization name for the registrant.
     * @param {number} params.periodYears - Registration term in years (1-10).
     * @param {boolean} params.autoRenewal - Whether the domain should renew automatically after purchase.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     */
    createPurchase(params: {
        domain: string;
        organizationId: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        billingAddressId: string;
        paymentMethodId: string;
        addressLine3?: string;
        companyName?: string;
        periodYears?: number;
        autoRenewal?: boolean;
    }): Promise<Models.DomainPurchase>;
    /**
     * Initiate a domain purchase by providing registrant details and a payment method. Authorizes the payment and returns a `clientSecret`. If 3D Secure is required, use the `clientSecret` on the client to complete the authentication challenge. Once authentication is complete (or if none is needed), call the Update Purchase endpoint to capture the payment and finalize the purchase.
     *
     * @param {string} domain - Fully qualified domain name to purchase (for example, example.com).
     * @param {string} organizationId - Team ID that will own the domain.
     * @param {string} firstName - Registrant first name used for domain registration.
     * @param {string} lastName - Registrant last name used for domain registration.
     * @param {string} email - Registrant email address for registration and notices.
     * @param {string} phone - Registrant phone number in E.164 format (for example, +15555551234).
     * @param {string} billingAddressId - Billing address ID used for registration contact details.
     * @param {string} paymentMethodId - Payment method ID to authorize and capture the purchase.
     * @param {string} addressLine3 - Additional address line for the registrant (line 3).
     * @param {string} companyName - Company or organization name for the registrant.
     * @param {number} periodYears - Registration term in years (1-10).
     * @param {boolean} autoRenewal - Whether the domain should renew automatically after purchase.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPurchase(
        domain: string,
        organizationId: string,
        firstName: string,
        lastName: string,
        email: string,
        phone: string,
        billingAddressId: string,
        paymentMethodId: string,
        addressLine3?: string,
        companyName?: string,
        periodYears?: number,
        autoRenewal?: boolean,
    ): Promise<Models.DomainPurchase>;
    createPurchase(
        paramsOrFirst:
            | {
                  domain: string;
                  organizationId: string;
                  firstName: string;
                  lastName: string;
                  email: string;
                  phone: string;
                  billingAddressId: string;
                  paymentMethodId: string;
                  addressLine3?: string;
                  companyName?: string;
                  periodYears?: number;
                  autoRenewal?: boolean;
              }
            | string,
        ...rest: [
            string?,
            string?,
            string?,
            string?,
            string?,
            string?,
            string?,
            string?,
            string?,
            number?,
            boolean?,
        ]
    ): Promise<Models.DomainPurchase> {
        let params: {
            domain: string;
            organizationId: string;
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            billingAddressId: string;
            paymentMethodId: string;
            addressLine3?: string;
            companyName?: string;
            periodYears?: number;
            autoRenewal?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                organizationId: string;
                firstName: string;
                lastName: string;
                email: string;
                phone: string;
                billingAddressId: string;
                paymentMethodId: string;
                addressLine3?: string;
                companyName?: string;
                periodYears?: number;
                autoRenewal?: boolean;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                organizationId: rest[0] as string,
                firstName: rest[1] as string,
                lastName: rest[2] as string,
                email: rest[3] as string,
                phone: rest[4] as string,
                billingAddressId: rest[5] as string,
                paymentMethodId: rest[6] as string,
                addressLine3: rest[7] as string,
                companyName: rest[8] as string,
                periodYears: rest[9] as number,
                autoRenewal: rest[10] as boolean,
            };
        }

        const domain = params.domain;
        const organizationId = params.organizationId;
        const firstName = params.firstName;
        const lastName = params.lastName;
        const email = params.email;
        const phone = params.phone;
        const billingAddressId = params.billingAddressId;
        const paymentMethodId = params.paymentMethodId;
        const addressLine3 = params.addressLine3;
        const companyName = params.companyName;
        const periodYears = params.periodYears;
        const autoRenewal = params.autoRenewal;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof organizationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "organizationId"',
            );
        }
        if (typeof firstName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "firstName"',
            );
        }
        if (typeof lastName === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "lastName"',
            );
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof phone === 'undefined') {
            throw new AppwriteException('Missing required parameter: "phone"');
        }
        if (typeof billingAddressId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "billingAddressId"',
            );
        }
        if (typeof paymentMethodId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        const apiPath = '/domains/purchases';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof organizationId !== 'undefined') {
            apiPayload['organizationId'] = organizationId;
        }
        if (typeof firstName !== 'undefined') {
            apiPayload['firstName'] = firstName;
        }
        if (typeof lastName !== 'undefined') {
            apiPayload['lastName'] = lastName;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof phone !== 'undefined') {
            apiPayload['phone'] = phone;
        }
        if (typeof billingAddressId !== 'undefined') {
            apiPayload['billingAddressId'] = billingAddressId;
        }
        if (typeof addressLine3 !== 'undefined') {
            apiPayload['addressLine3'] = addressLine3;
        }
        if (typeof companyName !== 'undefined') {
            apiPayload['companyName'] = companyName;
        }
        if (typeof periodYears !== 'undefined') {
            apiPayload['periodYears'] = periodYears;
        }
        if (typeof autoRenewal !== 'undefined') {
            apiPayload['autoRenewal'] = autoRenewal;
        }
        if (typeof paymentMethodId !== 'undefined') {
            apiPayload['paymentMethodId'] = paymentMethodId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPurchase',
            models: {
                domainPurchase: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domainId: '',
                        domain: '',
                        organizationId: '',
                        status: 'pending',
                        clientSecret: '',
                        amount: 0,
                        currency: 'USD',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Finalize a domain purchase initiated with Create Purchase. Verifies that any required 3D Secure authentication is complete, registers the domain, captures the payment, and provisions default DNS records. Returns a 402 error if authentication is still pending.
     *
     * @param {string} params.invoiceId - Invoice ID.
     * @param {string} params.organizationId - Team ID that owns the domain.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     */
    updatePurchase(params: {
        invoiceId: string;
        organizationId: string;
    }): Promise<Models.DomainPurchase>;
    /**
     * Finalize a domain purchase initiated with Create Purchase. Verifies that any required 3D Secure authentication is complete, registers the domain, captures the payment, and provisions default DNS records. Returns a 402 error if authentication is still pending.
     *
     * @param {string} invoiceId - Invoice ID.
     * @param {string} organizationId - Team ID that owns the domain.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePurchase(
        invoiceId: string,
        organizationId: string,
    ): Promise<Models.DomainPurchase>;
    updatePurchase(
        paramsOrFirst: { invoiceId: string; organizationId: string } | string,
        ...rest: [string?]
    ): Promise<Models.DomainPurchase> {
        let params: { invoiceId: string; organizationId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                invoiceId: string;
                organizationId: string;
            };
        } else {
            params = {
                invoiceId: paramsOrFirst as string,
                organizationId: rest[0] as string,
            };
        }

        const invoiceId = params.invoiceId;
        const organizationId = params.organizationId;

        if (typeof invoiceId === 'undefined' || invoiceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "invoiceId"',
            );
        }
        if (typeof organizationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "organizationId"',
            );
        }
        const apiPath = '/domains/purchases/{invoiceId}'.replace(
            '{invoiceId}',
            encodeURIComponent(String(invoiceId)),
        );
        const apiPayload: Payload = {};
        if (typeof organizationId !== 'undefined') {
            apiPayload['organizationId'] = organizationId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPurchase',
            models: {
                domainPurchase: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domainId: '',
                        domain: '',
                        organizationId: '',
                        status: 'pending',
                        clientSecret: '',
                        amount: 0,
                        currency: 'USD',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List domain suggestions.
     *
     * @param {string} params.query - Query to find available domains and suggestions. Max length: 256 chars.
     * @param {string[]} params.tlds - TLDs to suggest.
     * @param {number} params.limit - Maximum number of suggestions to return.
     * @param {DomainSuggestionType} params.filterType - Filter type: premium, suggestion.
     * @param {number} params.priceMax - Filter premium domains by maximum price. Only premium domains at or below this price will be returned. Does not affect regular domain suggestions.
     * @param {number} params.priceMin - Filter premium domains by minimum price. Only premium domains at or above this price will be returned. Does not affect regular domain suggestions.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainSuggestionsList>}
     */
    listSuggestions(params: {
        query: string;
        tlds?: string[];
        limit?: number;
        filterType?: DomainSuggestionType;
        priceMax?: number;
        priceMin?: number;
    }): Promise<Models.DomainSuggestionsList>;
    /**
     * List domain suggestions.
     *
     * @param {string} query - Query to find available domains and suggestions. Max length: 256 chars.
     * @param {string[]} tlds - TLDs to suggest.
     * @param {number} limit - Maximum number of suggestions to return.
     * @param {DomainSuggestionType} filterType - Filter type: premium, suggestion.
     * @param {number} priceMax - Filter premium domains by maximum price. Only premium domains at or below this price will be returned. Does not affect regular domain suggestions.
     * @param {number} priceMin - Filter premium domains by minimum price. Only premium domains at or above this price will be returned. Does not affect regular domain suggestions.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainSuggestionsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listSuggestions(
        query: string,
        tlds?: string[],
        limit?: number,
        filterType?: DomainSuggestionType,
        priceMax?: number,
        priceMin?: number,
    ): Promise<Models.DomainSuggestionsList>;
    listSuggestions(
        paramsOrFirst:
            | {
                  query: string;
                  tlds?: string[];
                  limit?: number;
                  filterType?: DomainSuggestionType;
                  priceMax?: number;
                  priceMin?: number;
              }
            | string,
        ...rest: [string[]?, number?, DomainSuggestionType?, number?, number?]
    ): Promise<Models.DomainSuggestionsList> {
        let params: {
            query: string;
            tlds?: string[];
            limit?: number;
            filterType?: DomainSuggestionType;
            priceMax?: number;
            priceMin?: number;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                query: string;
                tlds?: string[];
                limit?: number;
                filterType?: DomainSuggestionType;
                priceMax?: number;
                priceMin?: number;
            };
        } else {
            params = {
                query: paramsOrFirst as string,
                tlds: rest[0] as string[],
                limit: rest[1] as number,
                filterType: rest[2] as DomainSuggestionType,
                priceMax: rest[3] as number,
                priceMin: rest[4] as number,
            };
        }

        const query = params.query;
        const tlds = params.tlds;
        const limit = params.limit;
        const filterType = params.filterType;
        const priceMax = params.priceMax;
        const priceMin = params.priceMin;

        if (typeof query === 'undefined') {
            throw new AppwriteException('Missing required parameter: "query"');
        }
        const apiPath = '/domains/suggestions';
        const apiPayload: Payload = {};
        if (typeof query !== 'undefined') {
            apiPayload['query'] = query;
        }
        if (typeof tlds !== 'undefined') {
            apiPayload['tlds'] = tlds;
        }
        if (typeof limit !== 'undefined') {
            apiPayload['limit'] = limit;
        }
        if (typeof filterType !== 'undefined') {
            apiPayload['filterType'] = filterType;
        }
        if (typeof priceMax !== 'undefined') {
            apiPayload['priceMax'] = priceMax;
        }
        if (typeof priceMin !== 'undefined') {
            apiPayload['priceMin'] = priceMin;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainSuggestionsList',
            models: {
                domainSuggestionsList: {
                    defaults: {
                        total: 0,
                        suggestions: [],
                    },
                    properties: {
                        suggestions: {
                            items: 'domainSuggestion',
                        },
                    },
                },
                domainSuggestion: {
                    defaults: {
                        domain: '',
                        premium: false,
                        available: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Initiate a domain transfer-in by providing an authorization code, registrant details, and a payment method. Authorizes the payment and returns a `clientSecret`. If 3D Secure is required, use the `clientSecret` on the client to complete the authentication challenge. Once authentication is complete (or if none is needed), call the Update Transfer In endpoint to capture the payment and submit the transfer.
     *
     * @param {string} params.domain - Domain name to transfer in.
     * @param {string} params.organizationId - Organization ID that this domain will belong to.
     * @param {string} params.authCode - Authorization code for the domain transfer.
     * @param {string} params.paymentMethodId - Payment method ID to authorize and capture the transfer.
     * @param {boolean} params.autoRenewal - Whether the domain should renew automatically after transfer.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     */
    createTransferIn(params: {
        domain: string;
        organizationId: string;
        authCode: string;
        paymentMethodId: string;
        autoRenewal?: boolean;
    }): Promise<Models.DomainPurchase>;
    /**
     * Initiate a domain transfer-in by providing an authorization code, registrant details, and a payment method. Authorizes the payment and returns a `clientSecret`. If 3D Secure is required, use the `clientSecret` on the client to complete the authentication challenge. Once authentication is complete (or if none is needed), call the Update Transfer In endpoint to capture the payment and submit the transfer.
     *
     * @param {string} domain - Domain name to transfer in.
     * @param {string} organizationId - Organization ID that this domain will belong to.
     * @param {string} authCode - Authorization code for the domain transfer.
     * @param {string} paymentMethodId - Payment method ID to authorize and capture the transfer.
     * @param {boolean} autoRenewal - Whether the domain should renew automatically after transfer.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createTransferIn(
        domain: string,
        organizationId: string,
        authCode: string,
        paymentMethodId: string,
        autoRenewal?: boolean,
    ): Promise<Models.DomainPurchase>;
    createTransferIn(
        paramsOrFirst:
            | {
                  domain: string;
                  organizationId: string;
                  authCode: string;
                  paymentMethodId: string;
                  autoRenewal?: boolean;
              }
            | string,
        ...rest: [string?, string?, string?, boolean?]
    ): Promise<Models.DomainPurchase> {
        let params: {
            domain: string;
            organizationId: string;
            authCode: string;
            paymentMethodId: string;
            autoRenewal?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                organizationId: string;
                authCode: string;
                paymentMethodId: string;
                autoRenewal?: boolean;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                organizationId: rest[0] as string,
                authCode: rest[1] as string,
                paymentMethodId: rest[2] as string,
                autoRenewal: rest[3] as boolean,
            };
        }

        const domain = params.domain;
        const organizationId = params.organizationId;
        const authCode = params.authCode;
        const paymentMethodId = params.paymentMethodId;
        const autoRenewal = params.autoRenewal;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof organizationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "organizationId"',
            );
        }
        if (typeof authCode === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "authCode"',
            );
        }
        if (typeof paymentMethodId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        const apiPath = '/domains/transfers/in';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof organizationId !== 'undefined') {
            apiPayload['organizationId'] = organizationId;
        }
        if (typeof authCode !== 'undefined') {
            apiPayload['authCode'] = authCode;
        }
        if (typeof autoRenewal !== 'undefined') {
            apiPayload['autoRenewal'] = autoRenewal;
        }
        if (typeof paymentMethodId !== 'undefined') {
            apiPayload['paymentMethodId'] = paymentMethodId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPurchase',
            models: {
                domainPurchase: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domainId: '',
                        domain: '',
                        organizationId: '',
                        status: 'pending',
                        clientSecret: '',
                        amount: 0,
                        currency: 'USD',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Finalize a domain transfer-in initiated with Create Transfer In. Verifies that any required 3D Secure authentication is complete, submits the transfer with the authorization code, captures the payment, and sends a confirmation email. Returns a 402 error if authentication is still pending.
     *
     * @param {string} params.invoiceId - Invoice ID.
     * @param {string} params.organizationId - Team ID that owns the domain.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     */
    updateTransferIn(params: {
        invoiceId: string;
        organizationId: string;
    }): Promise<Models.DomainPurchase>;
    /**
     * Finalize a domain transfer-in initiated with Create Transfer In. Verifies that any required 3D Secure authentication is complete, submits the transfer with the authorization code, captures the payment, and sends a confirmation email. Returns a 402 error if authentication is still pending.
     *
     * @param {string} invoiceId - Invoice ID.
     * @param {string} organizationId - Team ID that owns the domain.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainPurchase>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateTransferIn(
        invoiceId: string,
        organizationId: string,
    ): Promise<Models.DomainPurchase>;
    updateTransferIn(
        paramsOrFirst: { invoiceId: string; organizationId: string } | string,
        ...rest: [string?]
    ): Promise<Models.DomainPurchase> {
        let params: { invoiceId: string; organizationId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                invoiceId: string;
                organizationId: string;
            };
        } else {
            params = {
                invoiceId: paramsOrFirst as string,
                organizationId: rest[0] as string,
            };
        }

        const invoiceId = params.invoiceId;
        const organizationId = params.organizationId;

        if (typeof invoiceId === 'undefined' || invoiceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "invoiceId"',
            );
        }
        if (typeof organizationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "organizationId"',
            );
        }
        const apiPath = '/domains/transfers/in/{invoiceId}'.replace(
            '{invoiceId}',
            encodeURIComponent(String(invoiceId)),
        );
        const apiPayload: Payload = {};
        if (typeof organizationId !== 'undefined') {
            apiPayload['organizationId'] = organizationId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainPurchase',
            models: {
                domainPurchase: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domainId: '',
                        domain: '',
                        organizationId: '',
                        status: 'pending',
                        clientSecret: '',
                        amount: 0,
                        currency: 'USD',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Initiate a domain transfer-out by generating an authorization code for the specified domain. The returned `authCode` should be provided to the gaining provider to complete the transfer. If the domain has auto-renewal enabled, it will be automatically disabled as part of this operation.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.organizationId - Organization ID that this domain belongs to.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainTransferOut>}
     */
    createTransferOut(params: {
        domainId: string;
        organizationId: string;
    }): Promise<Models.DomainTransferOut>;
    /**
     * Initiate a domain transfer-out by generating an authorization code for the specified domain. The returned `authCode` should be provided to the gaining provider to complete the transfer. If the domain has auto-renewal enabled, it will be automatically disabled as part of this operation.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} organizationId - Organization ID that this domain belongs to.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainTransferOut>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createTransferOut(
        domainId: string,
        organizationId: string,
    ): Promise<Models.DomainTransferOut>;
    createTransferOut(
        paramsOrFirst: { domainId: string; organizationId: string } | string,
        ...rest: [string?]
    ): Promise<Models.DomainTransferOut> {
        let params: { domainId: string; organizationId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                organizationId: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                organizationId: rest[0] as string,
            };
        }

        const domainId = params.domainId;
        const organizationId = params.organizationId;

        if (typeof domainId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof organizationId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "organizationId"',
            );
        }
        const apiPath = '/domains/transfers/out';
        const apiPayload: Payload = {};
        if (typeof domainId !== 'undefined') {
            apiPayload['domainId'] = domainId;
        }
        if (typeof organizationId !== 'undefined') {
            apiPayload['organizationId'] = organizationId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainTransferOut',
            models: {
                domainTransferOut: {
                    defaults: {
                        authCode: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a domain by its unique ID.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    get(params: { domainId: string }): Promise<Models.Domain>;
    /**
     * Get a domain by its unique ID.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    get(domainId: string): Promise<Models.Domain>;
    get(paramsOrFirst: { domainId: string } | string): Promise<Models.Domain> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a domain by its unique ID. This endpoint can be used to delete a domain from your project.
     * Once deleted, the domain will no longer be available for use and all associated resources will be removed.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(params: { domainId: string }): Promise<{}>;
    /**
     * Delete a domain by its unique ID. This endpoint can be used to delete a domain from your project.
     * Once deleted, the domain will no longer be available for use and all associated resources will be removed.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    delete(domainId: string): Promise<{}>;
    delete(paramsOrFirst: { domainId: string } | string): Promise<{}> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Enable or disable auto-renewal for a domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {boolean} params.autoRenewal - Whether the domain should renew automatically.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    updateAutoRenewal(params: {
        domainId: string;
        autoRenewal: boolean;
    }): Promise<Models.Domain>;
    /**
     * Enable or disable auto-renewal for a domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {boolean} autoRenewal - Whether the domain should renew automatically.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateAutoRenewal(
        domainId: string,
        autoRenewal: boolean,
    ): Promise<Models.Domain>;
    updateAutoRenewal(
        paramsOrFirst: { domainId: string; autoRenewal: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.Domain> {
        let params: { domainId: string; autoRenewal: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                autoRenewal: boolean;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                autoRenewal: rest[0] as boolean,
            };
        }

        const domainId = params.domainId;
        const autoRenewal = params.autoRenewal;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof autoRenewal === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "autoRenewal"',
            );
        }
        const apiPath = '/domains/{domainId}/auto-renewal'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof autoRenewal !== 'undefined') {
            apiPayload['autoRenewal'] = autoRenewal;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the registrar nameservers for the given domain. When nameservers are not provided,
     * the domain will be updated to use Appwrite nameservers.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string[]} params.nameservers - Nameservers to set for the domain. Defaults to Appwrite nameservers when omitted.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    updateNameservers(params: {
        domainId: string;
        nameservers?: string[];
    }): Promise<Models.Domain>;
    /**
     * Update the registrar nameservers for the given domain. When nameservers are not provided,
     * the domain will be updated to use Appwrite nameservers.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string[]} nameservers - Nameservers to set for the domain. Defaults to Appwrite nameservers when omitted.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateNameservers(
        domainId: string,
        nameservers?: string[],
    ): Promise<Models.Domain>;
    updateNameservers(
        paramsOrFirst: { domainId: string; nameservers?: string[] } | string,
        ...rest: [string[]?]
    ): Promise<Models.Domain> {
        let params: { domainId: string; nameservers?: string[] };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                nameservers?: string[];
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                nameservers: rest[0] as string[],
            };
        }

        const domainId = params.domainId;
        const nameservers = params.nameservers;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/nameservers'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof nameservers !== 'undefined') {
            apiPayload['nameservers'] = nameservers;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Verify which NS records are used and update the domain accordingly. This will check the domain's
     * nameservers and update the domain's status based on whether the nameservers match the expected
     * Appwrite nameservers.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    verifyNameservers(params: { domainId: string }): Promise<Models.Domain>;
    /**
     * Verify which NS records are used and update the domain accordingly. This will check the domain's
     * nameservers and update the domain's status based on whether the nameservers match the expected
     * Appwrite nameservers.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    verifyNameservers(domainId: string): Promise<Models.Domain>;
    verifyNameservers(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.Domain> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/nameservers/verification'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List Google Workspace DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetGoogleWorkspace(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List Google Workspace DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetGoogleWorkspace(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetGoogleWorkspace(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/google-workspace'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add Google Workspace DNS records to the domain. This will create the required MX records
     * for Google Workspace email hosting.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetGoogleWorkspace(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add Google Workspace DNS records to the domain. This will create the required MX records
     * for Google Workspace email hosting.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetGoogleWorkspace(
        domainId: string,
    ): Promise<Models.DnsRecordsList>;
    createPresetGoogleWorkspace(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/google-workspace'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List iCloud DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetICloud(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List iCloud DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetICloud(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetICloud(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/icloud'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add iCloud DNS records to the domain. This will create the required MX and SPF records
     * for using iCloud email services with your domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetICloud(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add iCloud DNS records to the domain. This will create the required MX and SPF records
     * for using iCloud email services with your domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetICloud(domainId: string): Promise<Models.DnsRecordsList>;
    createPresetICloud(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/icloud'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List Mailgun DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetMailgun(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List Mailgun DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetMailgun(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetMailgun(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/mailgun'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add Mailgun DNS records to the domain. This endpoint will create the required DNS records
     * for Mailgun in the specified domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetMailgun(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add Mailgun DNS records to the domain. This endpoint will create the required DNS records
     * for Mailgun in the specified domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetMailgun(domainId: string): Promise<Models.DnsRecordsList>;
    createPresetMailgun(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/mailgun'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List Outlook DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetOutlook(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List Outlook DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetOutlook(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetOutlook(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/outlook'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add Outlook DNS records to the domain. This will create the required MX records
     * for setting up Outlook email hosting for your domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetOutlook(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add Outlook DNS records to the domain. This will create the required MX records
     * for setting up Outlook email hosting for your domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetOutlook(domainId: string): Promise<Models.DnsRecordsList>;
    createPresetOutlook(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/outlook'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List ProtonMail DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetProtonMail(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List ProtonMail DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetProtonMail(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetProtonMail(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/proton-mail'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add ProtonMail DNS records to the domain. This will create the required MX records
     * for using ProtonMail with your custom domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetProtonMail(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add ProtonMail DNS records to the domain. This will create the required MX records
     * for using ProtonMail with your custom domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetProtonMail(domainId: string): Promise<Models.DnsRecordsList>;
    createPresetProtonMail(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/proton-mail'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List Zoho DNS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    getPresetZoho(params: { domainId: string }): Promise<Models.DnsRecordsList>;
    /**
     * List Zoho DNS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPresetZoho(domainId: string): Promise<Models.DnsRecordsList>;
    getPresetZoho(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/zoho'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add Zoho Mail DNS records to the domain. This will create the required MX records
     * for setting up Zoho Mail on your domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    createPresetZoho(params: {
        domainId: string;
    }): Promise<Models.DnsRecordsList>;
    /**
     * Add Zoho Mail DNS records to the domain. This will create the required MX records
     * for setting up Zoho Mail on your domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPresetZoho(domainId: string): Promise<Models.DnsRecordsList>;
    createPresetZoho(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/presets/zoho'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List DNS records for a given domain. You can use this endpoint to list all the DNS records
     * associated with your domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. You may filter on attributes such as type, name, value, etc. Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     */
    listRecords(params: {
        domainId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.DnsRecordsList>;
    /**
     * List DNS records for a given domain. You can use this endpoint to list all the DNS records
     * associated with your domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. You may filter on attributes such as type, name, value, etc. Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecordsList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listRecords(
        domainId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.DnsRecordsList>;
    listRecords(
        paramsOrFirst:
            { domainId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.DnsRecordsList> {
        let params: { domainId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const domainId = params.domainId;
        const queries = params.queries;
        const total = params.total;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/records'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecordsList',
            models: {
                dnsRecordsList: {
                    defaults: {
                        total: 0,
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new A record for the given domain. A records are used to point a domain name
     * to an IPv4 address. The record value should be a valid IPv4 address.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - IPv4 address for this A record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordA(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new A record for the given domain. A records are used to point a domain name
     * to an IPv4 address. The record value should be a valid IPv4 address.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - IPv4 address for this A record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordA(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordA(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/a'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing A record for the given domain. This endpoint allows you to modify
     * the properties of an A record including its name (subdomain), IPv4 address, TTL,
     * and optional comment.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - IPv4 address for this A record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordA(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing A record for the given domain. This endpoint allows you to modify
     * the properties of an A record including its name (subdomain), IPv4 address, TTL,
     * and optional comment.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - IPv4 address for this A record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordA(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordA(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/a/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new AAAA record for the given domain. This endpoint allows you to add a new IPv6 DNS record
     * to your domain. The record will be used to point a hostname to an IPv6 address.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - IPv6 address for this AAAA record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordAAAA(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new AAAA record for the given domain. This endpoint allows you to add a new IPv6 DNS record
     * to your domain. The record will be used to point a hostname to an IPv6 address.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - IPv6 address for this AAAA record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment explaining what this record is for.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordAAAA(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordAAAA(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/aaaa'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing AAAA record for the given domain. This endpoint allows you to modify
     * the properties of an existing AAAA record, including its name (subdomain), IPv6 address,
     * TTL, and optional comment.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - IPv6 address for this AAAA record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordAAAA(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing AAAA record for the given domain. This endpoint allows you to modify
     * the properties of an existing AAAA record, including its name (subdomain), IPv6 address,
     * TTL, and optional comment.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - IPv6 address for this AAAA record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordAAAA(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordAAAA(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/aaaa/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new ALIAS record for the given domain. This record type can be used to point your domain
     * to another domain name that will serve as an alias. This is particularly useful when you want to
     * map your domain to a target domain that may change its IP address.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name.
     * @param {string} params.value - Target domain for this ALIAS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordAlias(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new ALIAS record for the given domain. This record type can be used to point your domain
     * to another domain name that will serve as an alias. This is particularly useful when you want to
     * map your domain to a target domain that may change its IP address.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name.
     * @param {string} value - Target domain for this ALIAS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordAlias(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordAlias(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/alias'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing ALIAS record for the specified domain. This endpoint allows you to modify
     * the properties of an existing ALIAS record including its name, target domain, TTL, and comment.
     *
     * The ALIAS record type is similar to a CNAME record but can be used at the zone apex (root domain).
     * It provides a way to map one domain name to another.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name.
     * @param {string} params.value - Target domain for this ALIAS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordAlias(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing ALIAS record for the specified domain. This endpoint allows you to modify
     * the properties of an existing ALIAS record including its name, target domain, TTL, and comment.
     *
     * The ALIAS record type is similar to a CNAME record but can be used at the zone apex (root domain).
     * It provides a way to map one domain name to another.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name.
     * @param {string} value - Target domain for this ALIAS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordAlias(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordAlias(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/alias/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new CAA record for the given domain. CAA records are used to specify which
     * Certificate Authorities (CAs) are allowed to issue SSL/TLS certificates for your domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name.
     * @param {string} params.value - CAA value (e.g. issuer domain).
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordCAA(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new CAA record for the given domain. CAA records are used to specify which
     * Certificate Authorities (CAs) are allowed to issue SSL/TLS certificates for your domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name.
     * @param {string} value - CAA value (e.g. issuer domain).
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordCAA(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordCAA(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/caa'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing CAA record for the given domain. A CAA (Certification Authority Authorization)
     * record is used to specify which certificate authorities (CAs) are authorized to issue certificates
     * for a domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name.
     * @param {string} params.value - CAA value (e.g. issuer domain).
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordCAA(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing CAA record for the given domain. A CAA (Certification Authority Authorization)
     * record is used to specify which certificate authorities (CAs) are authorized to issue certificates
     * for a domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name.
     * @param {string} value - CAA value (e.g. issuer domain).
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordCAA(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordCAA(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/caa/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new CNAME record for the given domain.
     *
     * A CNAME record maps a subdomain to another domain name, allowing you to create aliases
     * for your domain. For example, you can create a CNAME record to point 'blog.example.com'
     * to 'example.wordpress.com'.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Canonical target for this CNAME record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordCNAME(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new CNAME record for the given domain.
     *
     * A CNAME record maps a subdomain to another domain name, allowing you to create aliases
     * for your domain. For example, you can create a CNAME record to point 'blog.example.com'
     * to 'example.wordpress.com'.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Canonical target for this CNAME record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordCNAME(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordCNAME(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/cname'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing CNAME record for the given domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Canonical target for this CNAME record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordCNAME(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing CNAME record for the given domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Canonical target for this CNAME record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordCNAME(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordCNAME(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/cname/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new HTTPS record for the given domain. This record is used to configure HTTPS
     * settings for your domain, enabling secure communication over SSL/TLS.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Target for the HTTPS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordHTTPS(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new HTTPS record for the given domain. This record is used to configure HTTPS
     * settings for your domain, enabling secure communication over SSL/TLS.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Target for the HTTPS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordHTTPS(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordHTTPS(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/https'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing HTTPS record for the given domain. This endpoint allows you to modify
     * the properties of an HTTPS record associated with your domain, including the name (subdomain),
     * target value, TTL, and optional comment.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Target for the HTTPS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordHTTPS(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing HTTPS record for the given domain. This endpoint allows you to modify
     * the properties of an HTTPS record associated with your domain, including the name (subdomain),
     * target value, TTL, and optional comment.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Target for the HTTPS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordHTTPS(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordHTTPS(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/https/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new MX record for the given domain. MX records are used to define the mail servers responsible
     * for accepting email messages for the domain. Multiple MX records can be created with different priorities.
     * The priority parameter determines the order in which mail servers are used, with lower values indicating
     * higher priority.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Mail server domain for this MX record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} params.priority - MX priority. Lower values are tried first.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordMX(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        priority: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new MX record for the given domain. MX records are used to define the mail servers responsible
     * for accepting email messages for the domain. Multiple MX records can be created with different priorities.
     * The priority parameter determines the order in which mail servers are used, with lower values indicating
     * higher priority.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Mail server domain for this MX record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} priority - MX priority. Lower values are tried first.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordMX(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        priority: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordMX(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  priority: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            priority: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                priority: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                priority: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const priority = params.priority;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        if (typeof priority === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "priority"',
            );
        }
        const apiPath = '/domains/{domainId}/records/mx'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof priority !== 'undefined') {
            apiPayload['priority'] = priority;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing MX record for the given domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Mail server domain for this MX record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} params.priority - MX priority. Lower values are tried first.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordMX(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        priority: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing MX record for the given domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Mail server domain for this MX record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} priority - MX priority. Lower values are tried first.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordMX(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        priority: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordMX(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  priority: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            priority: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                priority: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                priority: rest[4] as number,
                comment: rest[5] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const priority = params.priority;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        if (typeof priority === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "priority"',
            );
        }
        const apiPath = '/domains/{domainId}/records/mx/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof priority !== 'undefined') {
            apiPayload['priority'] = priority;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new NS record for the given domain. NS records specify the nameservers that are used
     * to resolve the domain name to IP addresses. Each domain can have multiple NS records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Nameserver target for this NS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordNS(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new NS record for the given domain. NS records specify the nameservers that are used
     * to resolve the domain name to IP addresses. Each domain can have multiple NS records.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Nameserver target for this NS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordNS(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordNS(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/ns'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing NS record for the given domain. This endpoint allows you to modify
     * the properties of an NS (nameserver) record associated with your domain. You can update
     * the record name (subdomain), target nameserver value, TTL, and add or modify comments
     * for better record management.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain).
     * @param {string} params.value - Nameserver target for this NS record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordNS(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing NS record for the given domain. This endpoint allows you to modify
     * the properties of an NS (nameserver) record associated with your domain. You can update
     * the record name (subdomain), target nameserver value, TTL, and add or modify comments
     * for better record management.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain).
     * @param {string} value - Nameserver target for this NS record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordNS(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordNS(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/ns/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new SRV record for the given domain. SRV records are used to define the location
     * of servers for specific services. For example, they can be used to specify which server
     * handles a specific service like SIP or XMPP for the domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (service name).
     * @param {string} params.value - Target hostname for this SRV record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} params.priority - Record priority. Lower values are tried first.
     * @param {number} params.weight - Record weight, used to share load between targets of equal priority.
     * @param {number} params.port - Port number for the service.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordSRV(params: {
        domainId: string;
        name: string;
        value: string;
        ttl: number;
        priority: number;
        weight: number;
        port: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new SRV record for the given domain. SRV records are used to define the location
     * of servers for specific services. For example, they can be used to specify which server
     * handles a specific service like SIP or XMPP for the domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (service name).
     * @param {string} value - Target hostname for this SRV record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} priority - Record priority. Lower values are tried first.
     * @param {number} weight - Record weight, used to share load between targets of equal priority.
     * @param {number} port - Port number for the service.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordSRV(
        domainId: string,
        name: string,
        value: string,
        ttl: number,
        priority: number,
        weight: number,
        port: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordSRV(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  priority: number;
                  weight: number;
                  port: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, number?, number?, number?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            value: string;
            ttl: number;
            priority: number;
            weight: number;
            port: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                value: string;
                ttl: number;
                priority: number;
                weight: number;
                port: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                value: rest[1] as string,
                ttl: rest[2] as number,
                priority: rest[3] as number,
                weight: rest[4] as number,
                port: rest[5] as number,
                comment: rest[6] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const priority = params.priority;
        const weight = params.weight;
        const port = params.port;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        if (typeof priority === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "priority"',
            );
        }
        if (typeof weight === 'undefined') {
            throw new AppwriteException('Missing required parameter: "weight"');
        }
        if (typeof port === 'undefined') {
            throw new AppwriteException('Missing required parameter: "port"');
        }
        const apiPath = '/domains/{domainId}/records/srv'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof priority !== 'undefined') {
            apiPayload['priority'] = priority;
        }
        if (typeof weight !== 'undefined') {
            apiPayload['weight'] = weight;
        }
        if (typeof port !== 'undefined') {
            apiPayload['port'] = port;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing SRV record for the given domain.
     *
     * Required parameters:
     * - domainId: Domain unique ID
     * - recordId: DNS record unique ID
     * - name: Record name (service name)
     * - value: Target hostname for this SRV record
     * - ttl: Time to live, in seconds
     * - priority: Record priority
     * - weight: Record weight
     * - port: Port number for the service
     *
     * Optional parameters:
     * - comment: A comment for this record
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (service name).
     * @param {string} params.value - Target hostname for this SRV record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} params.priority - Record priority. Lower values are tried first.
     * @param {number} params.weight - Record weight, used to share load between targets of equal priority.
     * @param {number} params.port - Port number for the service.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordSRV(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        priority: number;
        weight: number;
        port: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing SRV record for the given domain.
     *
     * Required parameters:
     * - domainId: Domain unique ID
     * - recordId: DNS record unique ID
     * - name: Record name (service name)
     * - value: Target hostname for this SRV record
     * - ttl: Time to live, in seconds
     * - priority: Record priority
     * - weight: Record weight
     * - port: Port number for the service
     *
     * Optional parameters:
     * - comment: A comment for this record
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (service name).
     * @param {string} value - Target hostname for this SRV record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {number} priority - Record priority. Lower values are tried first.
     * @param {number} weight - Record weight, used to share load between targets of equal priority.
     * @param {number} port - Port number for the service.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordSRV(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        priority: number,
        weight: number,
        port: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordSRV(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  priority: number;
                  weight: number;
                  port: number;
                  comment?: string;
              }
            | string,
        ...rest: [
            string?,
            string?,
            string?,
            number?,
            number?,
            number?,
            number?,
            string?,
        ]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            priority: number;
            weight: number;
            port: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                priority: number;
                weight: number;
                port: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                priority: rest[4] as number,
                weight: rest[5] as number,
                port: rest[6] as number,
                comment: rest[7] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const priority = params.priority;
        const weight = params.weight;
        const port = params.port;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        if (typeof priority === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "priority"',
            );
        }
        if (typeof weight === 'undefined') {
            throw new AppwriteException('Missing required parameter: "weight"');
        }
        if (typeof port === 'undefined') {
            throw new AppwriteException('Missing required parameter: "port"');
        }
        const apiPath = '/domains/{domainId}/records/srv/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof priority !== 'undefined') {
            apiPayload['priority'] = priority;
        }
        if (typeof weight !== 'undefined') {
            apiPayload['weight'] = weight;
        }
        if (typeof port !== 'undefined') {
            apiPayload['port'] = port;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new TXT record for the given domain. TXT records can be used
     * to provide additional information about your domain, such as domain
     * verification records, SPF records, or DKIM records.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.name - Record name (subdomain) for the TXT record.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.value - TXT record value.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    createRecordTXT(params: {
        domainId: string;
        name: string;
        ttl: number;
        value?: string;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Create a new TXT record for the given domain. TXT records can be used
     * to provide additional information about your domain, such as domain
     * verification records, SPF records, or DKIM records.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} name - Record name (subdomain) for the TXT record.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} value - TXT record value.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecordTXT(
        domainId: string,
        name: string,
        ttl: number,
        value?: string,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    createRecordTXT(
        paramsOrFirst:
            | {
                  domainId: string;
                  name: string;
                  ttl: number;
                  value?: string;
                  comment?: string;
              }
            | string,
        ...rest: [string?, number?, string?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            name: string;
            ttl: number;
            value?: string;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                name: string;
                ttl: number;
                value?: string;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                name: rest[0] as string,
                ttl: rest[1] as number,
                value: rest[2] as string,
                comment: rest[3] as string,
            };
        }

        const domainId = params.domainId;
        const name = params.name;
        const ttl = params.ttl;
        const value = params.value;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/txt'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an existing TXT record for the given domain.
     *
     * Update the TXT record details for a specific domain by providing the domain ID,
     * record ID, and the new record configuration including name, value, TTL, and an optional comment.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @param {string} params.name - Record name (subdomain) for the TXT record.
     * @param {string} params.value - TXT record value.
     * @param {number} params.ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} params.comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    updateRecordTXT(params: {
        domainId: string;
        recordId: string;
        name: string;
        value: string;
        ttl: number;
        comment?: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Update an existing TXT record for the given domain.
     *
     * Update the TXT record details for a specific domain by providing the domain ID,
     * record ID, and the new record configuration including name, value, TTL, and an optional comment.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @param {string} name - Record name (subdomain) for the TXT record.
     * @param {string} value - TXT record value.
     * @param {number} ttl - Time to live, in seconds. Must be between 1 and 2147483647.
     * @param {string} comment - A comment for this record.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecordTXT(
        domainId: string,
        recordId: string,
        name: string,
        value: string,
        ttl: number,
        comment?: string,
    ): Promise<Models.DnsRecord>;
    updateRecordTXT(
        paramsOrFirst:
            | {
                  domainId: string;
                  recordId: string;
                  name: string;
                  value: string;
                  ttl: number;
                  comment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.DnsRecord> {
        let params: {
            domainId: string;
            recordId: string;
            name: string;
            value: string;
            ttl: number;
            comment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
                name: string;
                value: string;
                ttl: number;
                comment?: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
                name: rest[1] as string,
                value: rest[2] as string,
                ttl: rest[3] as number,
                comment: rest[4] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;
        const name = params.name;
        const value = params.value;
        const ttl = params.ttl;
        const comment = params.comment;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof ttl === 'undefined') {
            throw new AppwriteException('Missing required parameter: "ttl"');
        }
        const apiPath = '/domains/{domainId}/records/txt/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        if (typeof comment !== 'undefined') {
            apiPayload['comment'] = comment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a single DNS record for a given domain by record ID.
     *
     * This endpoint allows you to retrieve a specific DNS record associated with a domain
     * using its unique identifier. The record contains information about the DNS configuration
     * such as type, value, and TTL settings.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     */
    getRecord(params: {
        domainId: string;
        recordId: string;
    }): Promise<Models.DnsRecord>;
    /**
     * Get a single DNS record for a given domain by record ID.
     *
     * This endpoint allows you to retrieve a specific DNS record associated with a domain
     * using its unique identifier. The record contains information about the DNS configuration
     * such as type, value, and TTL settings.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DnsRecord>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getRecord(domainId: string, recordId: string): Promise<Models.DnsRecord>;
    getRecord(
        paramsOrFirst: { domainId: string; recordId: string } | string,
        ...rest: [string?]
    ): Promise<Models.DnsRecord> {
        let params: { domainId: string; recordId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        const apiPath = '/domains/{domainId}/records/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'dnsRecord',
            models: {
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a DNS record for the given domain. This endpoint allows you to delete an existing DNS record
     * from a specific domain.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.recordId - DNS record unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteRecord(params: { domainId: string; recordId: string }): Promise<{}>;
    /**
     * Delete a DNS record for the given domain. This endpoint allows you to delete an existing DNS record
     * from a specific domain.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} recordId - DNS record unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteRecord(domainId: string, recordId: string): Promise<{}>;
    deleteRecord(
        paramsOrFirst: { domainId: string; recordId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { domainId: string; recordId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                recordId: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                recordId: rest[0] as string,
            };
        }

        const domainId = params.domainId;
        const recordId = params.recordId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof recordId === 'undefined' || recordId === '') {
            throw new AppwriteException(
                'Missing required parameter: "recordId"',
            );
        }
        const apiPath = '/domains/{domainId}/records/{recordId}'
            .replace('{domainId}', encodeURIComponent(String(domainId)))
            .replace('{recordId}', encodeURIComponent(String(recordId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the team ID for a specific domain. The caller must administer the current
     * team and be an owner of the destination team.
     *
     * Updating the team ID will transfer ownership and access control of the domain
     * and all its DNS records to the new team.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.teamId - New team unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    updateTeam(params: {
        domainId: string;
        teamId: string;
    }): Promise<Models.Domain>;
    /**
     * Update the team ID for a specific domain. The caller must administer the current
     * team and be an owner of the destination team.
     *
     * Updating the team ID will transfer ownership and access control of the domain
     * and all its DNS records to the new team.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} teamId - New team unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateTeam(domainId: string, teamId: string): Promise<Models.Domain>;
    updateTeam(
        paramsOrFirst: { domainId: string; teamId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Domain> {
        let params: { domainId: string; teamId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                teamId: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                teamId: rest[0] as string,
            };
        }

        const domainId = params.domainId;
        const teamId = params.teamId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof teamId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "teamId"');
        }
        const apiPath = '/domains/{domainId}/team'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof teamId !== 'undefined') {
            apiPayload['teamId'] = teamId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Retrieve the current transfer status for a domain. Returns the status, an optional reason, and a timestamp of the last status change.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainTransferStatus>}
     */
    getTransferStatus(params: {
        domainId: string;
    }): Promise<Models.DomainTransferStatus>;
    /**
     * Retrieve the current transfer status for a domain. Returns the status, an optional reason, and a timestamp of the last status change.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DomainTransferStatus>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getTransferStatus(domainId: string): Promise<Models.DomainTransferStatus>;
    getTransferStatus(
        paramsOrFirst: { domainId: string } | string,
    ): Promise<Models.DomainTransferStatus> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/transfers/status'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domainTransferStatus',
            models: {
                domainTransferStatus: {
                    defaults: {
                        status: 'pending_registry',
                        reason: '',
                        timestamp: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Retrieve the DNS zone file for the given domain. This endpoint will return the DNS
     * zone file in a standardized format that can be used to configure DNS servers.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<string>}
     */
    getZone(params: { domainId: string }): Promise<string>;
    /**
     * Retrieve the DNS zone file for the given domain. This endpoint will return the DNS
     * zone file in a standardized format that can be used to configure DNS servers.
     *
     * @param {string} domainId - Domain unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<string>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getZone(domainId: string): Promise<string>;
    getZone(paramsOrFirst: { domainId: string } | string): Promise<string> {
        let params: { domainId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domainId: string };
        } else {
            params = {
                domainId: paramsOrFirst as string,
            };
        }

        const domainId = params.domainId;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        const apiPath = '/domains/{domainId}/zone'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'text/plain',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload, 'text');
    }

    /**
     * Update the DNS zone for the given domain using the provided zone file content.
     * All parsed records are imported and then the main domain document is returned.
     *
     * @param {string} params.domainId - Domain unique ID.
     * @param {string} params.content - DNS zone file content as a string.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     */
    updateZone(params: {
        domainId: string;
        content: string;
    }): Promise<Models.Domain>;
    /**
     * Update the DNS zone for the given domain using the provided zone file content.
     * All parsed records are imported and then the main domain document is returned.
     *
     * @param {string} domainId - Domain unique ID.
     * @param {string} content - DNS zone file content as a string.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Domain>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateZone(domainId: string, content: string): Promise<Models.Domain>;
    updateZone(
        paramsOrFirst: { domainId: string; content: string } | string,
        ...rest: [string?]
    ): Promise<Models.Domain> {
        let params: { domainId: string; content: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domainId: string;
                content: string;
            };
        } else {
            params = {
                domainId: paramsOrFirst as string,
                content: rest[0] as string,
            };
        }

        const domainId = params.domainId;
        const content = params.content;

        if (typeof domainId === 'undefined' || domainId === '') {
            throw new AppwriteException(
                'Missing required parameter: "domainId"',
            );
        }
        if (typeof content === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "content"',
            );
        }
        const apiPath = '/domains/{domainId}/zone'.replace(
            '{domainId}',
            encodeURIComponent(String(domainId)),
        );
        const apiPayload: Payload = {};
        if (typeof content !== 'undefined') {
            apiPayload['content'] = content;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'domain',
            models: {
                domain: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        registrar: '',
                        nameservers: '',
                        expire: '',
                        renewal: '',
                        autoRenewal: false,
                        renewalPrice: 0,
                        teamId: '',
                        dnsRecords: [],
                    },
                    properties: {
                        dnsRecords: {
                            items: 'dnsRecord',
                        },
                    },
                },
                dnsRecord: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        name: '',
                        value: '',
                        ttl: 3600,
                        priority: 0,
                        lock: false,
                        weight: 0,
                        port: 0,
                        comment: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
